import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DBCollection, DBSentence, Table } from '../models/db_models';
import { deleteById, getById, getByIndex } from '../utils/dao';
import { SPEECH_STORE } from '../stores/SpeechStore';

function Collection() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [collection, setCollection] = useState<DBCollection | null>(null);
  const [sentenceCount, setSentenceCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([]);

  useEffect(() => {
    const loadData = async () => {
      if (!id) {
        setError('No collection ID provided');
        setLoading(false);
        return;
      }

      try {
        const col = await getById(Table.COLLECTIONS, id) as DBCollection | undefined;
        if (!col) {
          setError('Collection not found');
          setLoading(false);
          return;
        }

        const sents = await getByIndex(Table.SENTENCES, 'collectionId', id) as DBSentence[];
        setCollection(col);
        setSentenceCount(sents.length);

        // Load available voices for the target language
        const voices = SPEECH_STORE.getVoices(col.targetLanguage);
        setAvailableVoices(voices);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const handleDelete = async () => {
    if (!id || !collection) return;

    const userInput = prompt('Type "YES" to really delete this collection:');
    if (userInput !== 'YES') {
      return;
    }

    try {
      await deleteById(Table.COLLECTIONS, id);
      navigate('/');
    } catch (err) {
      alert(`Failed to delete collection: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  if (loading) {
    return (
      <div className="container text-center mt-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading sentences...</span>
        </div>
        <p className="mt-3">Loading sentences...</p>
      </div>
    );
  }

  if (error || !collection) {
    return (
      <div className="container text-center mt-5">
        <p className="text-danger">Error: {error || 'Collection not found'}</p>
        <p className="mt-4">
          <Link to="/" className="text-primary">← Back to Home</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="text-center">
        <h1 className="mb-3">Collection: {collection.knownLanguage} → {collection.targetLanguage}</h1>
        <p className="text-muted mb-2">
          {sentenceCount.toLocaleString()} sentences | Type: {collection.type}
        </p>
        <p className="text-success fs-5 mb-5">
          Done: <strong>{collection.doneCount || 0}</strong> / {sentenceCount}
          {' '}({Math.round(((collection.doneCount || 0) / sentenceCount) * 100)}%)
        </p>

        <div className="d-flex flex-column gap-3 mx-auto" style={{ maxWidth: '400px' }}>
          <Link
            to={`/collection/${id}/quiz`}
            className="btn btn-success btn-lg fw-bold"
          >
            Start Quiz
          </Link>

          {availableVoices.length > 1 && (
            <Link
              to={`/collection/${id}/voices`}
              className="btn btn-primary"
            >
              Select Voices ({
                collection.enabledVoices && collection.enabledVoices.length > 0
                  ? `${collection.enabledVoices.length} of ${availableVoices.length} selected`
                  : `${availableVoices.length} of ${availableVoices.length} selected`
              })
            </Link>
          )}

          <button
            onClick={handleDelete}
            className="btn btn-danger"
          >
            Delete Collection
          </button>

          <Link
            to="/"
            className="btn btn-secondary"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Collection;
